import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  Skeleton,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import { colors, typography } from '../../../styles/theme';
import {
  APP_MODAL_DIVIDER,
  APP_MODAL_TEXT,
  appModalActionsSx,
  appModalCloseButtonSx,
  appModalContentSx,
  appModalFieldSx,
  appModalHeaderDividerSx,
  appModalPaperSx,
  appModalSecondaryButtonSx,
  appModalSubtitleSx,
  appModalTitleBarSx,
  appModalTitleSx,
} from '../../../Components/appModalChrome.styles';
import { toast } from '../../../Components/Toast';
import CollectionInquiryBadge from '../../../Components/CollectionInquiryBadge';
import {
  getCollectionInquiries,
  isCollectionInquiryInboxUnavailable,
  readCollectionInquiries,
  updateCollectionInquiry,
} from '../../../services/collectionInquiryService';
import {
  INQUIRY_SEEN_FILTER_OPTIONS,
  filterCollectionInquiryRows,
  isCollectionInquiryUnseen,
} from '../../utils/collectionInquiryDisplay';
import { getScrollEdgeFadeSx, useScrollEdgeFade } from '../../../hooks/useScrollEdgeFade';
import { readInquiryCanViewLeads } from '../../../services/leadsService';
import { groupInquiriesByLead } from '../../../Leads/inquiryLeadGroups';
import InquiryCard from '../../../Leads/components/InquiryCard';
import InquiryLeadGroup from '../../../Leads/components/InquiryLeadGroup';

/** Reexport para tests / montajes que importaban la tarjeta desde el diálogo. */
export { default as InquiryCard } from '../../../Leads/components/InquiryCard';

const PAGE_SIZE = 20;
const skRowSx = { bgcolor: 'rgba(255,255,255,0.08)', borderRadius: '8px' };

const selectSx = {
  minWidth: 132,
  height: 40,
  borderRadius: '8px',
  fontSize: '14px',
  fontFamily: typography.fontFamily.primary,
  color: APP_MODAL_TEXT,
  bgcolor: colors.backgroundSurface,
  '& .MuiOutlinedInput-notchedOutline': { borderColor: APP_MODAL_DIVIDER },
  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: APP_MODAL_DIVIDER },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: colors.primary },
  '& .MuiSelect-icon': { color: colors.textSecondary },
  '& .MuiSelect-select': { py: '10px', px: '12px' },
};

const menuPaperSx = {
  bgcolor: colors.backgroundSurface,
  backgroundImage: 'none',
  border: `1px solid ${APP_MODAL_DIVIDER}`,
};

const hiddenScrollbarSx = {
  scrollbarWidth: 'none',
  msOverflowStyle: 'none',
  '&::-webkit-scrollbar': { width: 0, height: 0, display: 'none' },
};

function InquiryListSkeleton({ count = 4 }) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }} aria-hidden>
      {Array.from({ length: count }, (_, key) => (
        <Box
          key={key}
          sx={{
            display: 'flex',
            gap: 1.25,
            minHeight: 88,
            px: 1.25,
            py: 1.25,
            borderRadius: '8px',
            bgcolor: colors.backgroundDark,
          }}
        >
          <Skeleton variant="circular" width={32} height={32} sx={skRowSx} />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Skeleton variant="text" width="48%" height={16} sx={skRowSx} />
            <Skeleton variant="text" width="62%" height={12} sx={skRowSx} />
          </Box>
        </Box>
      ))}
    </Box>
  );
}

/**
 * Modal "LEADS Y CONSULTAS" de una lista / showroom (antes CONSULTAS /
 * `CollectionInquiryDialog`). GET `/getcollectioninquiries`; con
 * `can_view_leads` agrupa por lead → registro; sin él, bandeja plana.
 *
 * @param {{
 *   open: boolean,
 *   kind?: string,
 *   collectionId: string,
 *   collectionName?: string,
 *   ownerUserId?: string|null,
 *   onClose: () => void,
 *   onOpenItem?: (target: object) => void,
 *   onOpenLead?: (leadId: string) => void,
 *   onStatsChange?: (stats: object) => void,
 * }} props
 */
export default function CollectionLeadsDialog({
  open,
  kind = 'list',
  collectionId,
  collectionName = '',
  onClose,
  onOpenItem,
  onStatsChange,
  ownerUserId = null,
  onOpenLead,
}) {
  void ownerUserId; // lo pasan los callers (espacio ajeno); la bandeja ya no escribe leads

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [nextBefore, setNextBefore] = useState('');
  const [unseenTotal, setUnseenTotal] = useState(0);
  const [inquiryTotal, setInquiryTotal] = useState(0);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');
  const [seenFilter, setSeenFilter] = useState('all');
  const [expandedIds, setExpandedIds] = useState(() => new Set());
  const [markingId, setMarkingId] = useState('');
  const [canViewLeads, setCanViewLeads] = useState(false);
  const listRef = useRef(null);
  const sentinelRef = useRef(null);
  const { scrollRef: fadeRef, fadeTop, fadeBottom, refreshScrollEdges } = useScrollEdgeFade();
  const setListNode = useCallback((node) => {
    listRef.current = node;
    fadeRef(node);
  }, [fadeRef]);
  const onStatsChangeRef = useRef(onStatsChange);
  onStatsChangeRef.current = onStatsChange;

  const buildQuery = useCallback((before = '') => {
    const base = kind === 'cad'
      ? { projectId: collectionId, limit: PAGE_SIZE }
      : { listId: collectionId, limit: PAGE_SIZE };
    return {
      ...base,
      ...(before ? { before } : {}),
      ...(seenFilter === 'unseen' ? { unseen: true } : {}),
    };
  }, [collectionId, kind, seenFilter]);

  useEffect(() => {
    if (!open) return;
    setQuery('');
    setExpandedIds(new Set());
  }, [open, collectionId]);

  useEffect(() => {
    if (!open || !collectionId) return undefined;
    const controller = new AbortController();
    setLoading(true);
    setError('');
    setRows([]);
    setHasMore(false);
    setNextBefore('');
    setUnseenTotal(0);
    setInquiryTotal(0);
    setCanViewLeads(false);
    (async () => {
      try {
        const res = await getCollectionInquiries(buildQuery(), { signal: controller.signal });
        if (controller.signal.aborted) return;
        setCanViewLeads(readInquiryCanViewLeads(res));
        if (isCollectionInquiryInboxUnavailable(res._httpStatus)) {
          setRows([]);
          setUnseenTotal(0);
          setInquiryTotal(0);
          onStatsChangeRef.current?.({ inquiry_total: 0, unseen_total: 0, by_item: {} });
          return;
        }
        if (!res._httpOk) {
          setError(res.error || res.message || 'No se pudieron cargar las consultas.');
          return;
        }
        const parsed = readCollectionInquiries(res);
        setRows(parsed.rows);
        setHasMore(parsed.has_more);
        setNextBefore(parsed.next_before);
        setUnseenTotal(parsed.unseen_total);
        setInquiryTotal(parsed.inquiry_total);
        onStatsChangeRef.current?.(parsed);
      } catch (err) {
        if (err?.name === 'AbortError') return;
        console.error('Consultas: GET /getcollectioninquiries falló', err);
        setError('No se pudieron cargar las consultas.');
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    })();
    return () => controller.abort();
  }, [open, collectionId, kind, seenFilter, buildQuery]);

  const handleLoadMore = useCallback(async () => {
    if (!collectionId || !hasMore || !nextBefore || loadingMore || loading) return;
    setLoadingMore(true);
    setError('');
    try {
      const res = await getCollectionInquiries(buildQuery(nextBefore));
      if (isCollectionInquiryInboxUnavailable(res._httpStatus) || !res._httpOk) {
        setHasMore(false);
        if (!isCollectionInquiryInboxUnavailable(res._httpStatus)) {
          setError(res.error || res.message || 'No se pudieron cargar las consultas.');
        }
        return;
      }
      const parsed = readCollectionInquiries(res);
      setRows((prev) => [...prev, ...parsed.rows]);
      setHasMore(parsed.has_more);
      setNextBefore(parsed.next_before);
      setUnseenTotal(parsed.unseen_total);
      setInquiryTotal(parsed.inquiry_total);
      onStatsChangeRef.current?.(parsed);
    } catch (err) {
      console.error('Consultas: GET más falló', err);
      setError('No se pudieron cargar las consultas.');
      setHasMore(false);
    } finally {
      setLoadingMore(false);
    }
  }, [buildQuery, collectionId, hasMore, loading, loadingMore, nextBefore]);

  const markSeen = useCallback(async (row) => {
    const id = String(row?.id || '').trim();
    if (!id || !isCollectionInquiryUnseen(row) || markingId) return;
    setMarkingId(id);
    try {
      const res = await updateCollectionInquiry({ id, seen: true });
      if (!res._httpOk) {
        toast.error(res.error || res.message || 'No se pudo marcar la consulta como vista.');
        return;
      }
      const updated = res.data && typeof res.data === 'object' ? res.data : { ...row, seen_at: new Date().toISOString() };
      setRows((prev) => prev.map((item) => (item.id === id ? { ...item, ...updated } : item)));
      setUnseenTotal((count) => {
        const next = Math.max(0, count - 1);
        onStatsChangeRef.current?.({ unseen_total: next });
        return next;
      });
    } catch (err) {
      if (err?.name === 'AbortError') return;
      console.error('Consultas: PATCH /updatecollectioninquiry falló', err);
      toast.error('No se pudo marcar la consulta como vista.');
    } finally {
      setMarkingId('');
    }
  }, [markingId]);

  const filteredRows = useMemo(
    () => filterCollectionInquiryRows(rows, { query, seen: seenFilter }),
    [rows, query, seenFilter],
  );
  useEffect(() => {
    const sentinel = sentinelRef.current;
    const root = listRef.current;
    if (!open || !sentinel || !hasMore || loading || loadingMore || error) return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) handleLoadMore();
      },
      { root, rootMargin: '96px' },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [open, hasMore, loading, loadingMore, error, handleLoadMore, filteredRows.length]);
  useEffect(() => {
    refreshScrollEdges();
  }, [filteredRows, expandedIds, loading, loadingMore, refreshScrollEdges]);

  const toggleExpanded = (row) => {
    const rowId = String(row?.id || '');
    if (!rowId) return;
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(rowId)) next.delete(rowId);
      else next.add(rowId);
      return next;
    });
    markSeen(row);
  };

  const { groups: leadGroups, orphans } = useMemo(() => {
    if (!canViewLeads) return { groups: [], orphans: filteredRows };
    return groupInquiriesByLead(filteredRows, []);
  }, [canViewLeads, filteredRows]);

  const filtersActive = Boolean(query.trim()) || seenFilter !== 'all';
  const emptyMessage = rows.length === 0
    ? 'Todavía no hay consultas en esta lista.'
    : (filtersActive
      ? 'No hay consultas con estos filtros.'
      : 'No hay consultas.');

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      PaperProps={{ sx: appModalPaperSx }}
    >
      <DialogTitle sx={appModalTitleBarSx}>
        <Typography component="h2" sx={appModalTitleSx}>LEADS Y CONSULTAS</Typography>
        <IconButton
          aria-label="Cerrar"
          onClick={onClose}
          sx={appModalCloseButtonSx}
        >
          <CloseIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </DialogTitle>
      <Divider sx={appModalHeaderDividerSx} />
      <Box
        sx={{
          ...appModalSubtitleSx,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1.5,
          minWidth: 0,
        }}
      >
        <Typography sx={{ ...appModalSubtitleSx, mt: 0, px: 0, mb: 0, minWidth: 0 }}>
          {collectionName ? (
            <>
              {'Leads y consultas de la lista '}
              <Box
                component="span"
                sx={{
                  fontWeight: 700,
                  fontSize: '15px',
                  lineHeight: 1.3,
                  fontFamily: typography.fontFamily.primary,
                }}
              >
                {collectionName}
              </Box>
              .
            </>
          ) : (
            'Leads y consultas de esta lista.'
          )}
        </Typography>
        <CollectionInquiryBadge
          total={inquiryTotal}
          unseen={unseenTotal}
          sx={{ flexShrink: 0 }}
        />
      </Box>
      <DialogContent sx={{ ...appModalContentSx, gap: 1.25 }}>
        <Box
          role="search"
          aria-label="Filtrar leads y consultas"
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.75,
            flexShrink: 0,
            minWidth: 0,
          }}
        >
          <TextField
            size="small"
            placeholder="Buscar por lote, nombre o email"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            sx={{ ...appModalFieldSx, flex: 1, minWidth: 0 }}
            inputProps={{ maxLength: 80, 'aria-label': 'Buscar en las consultas' }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 18, color: colors.textSecondary }} />
                </InputAdornment>
              ),
            }}
          />
          <Select
            size="small"
            value={seenFilter}
            onChange={(event) => setSeenFilter(event.target.value)}
            aria-label="Filtrar por estado"
            sx={{ ...selectSx, flexShrink: 0 }}
            MenuProps={{ PaperProps: { sx: menuPaperSx } }}
          >
            {INQUIRY_SEEN_FILTER_OPTIONS.map((option) => (
              <MenuItem
                key={option.id}
                value={option.id}
                sx={{
                  fontFamily: typography.fontFamily.primary,
                  fontSize: '14px',
                  color: APP_MODAL_TEXT,
                }}
              >
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </Box>
        {error ? (
          <Typography sx={{ color: colors.error, fontSize: '12px', flexShrink: 0 }}>
            {error}
          </Typography>
        ) : null}
        <Box
          ref={setListNode}
          aria-busy={loading || loadingMore || undefined}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1.25,
            flex: 1,
            minHeight: 0,
            overflowY: 'auto',
            ...hiddenScrollbarSx,
            ...getScrollEdgeFadeSx(fadeTop, fadeBottom),
          }}
        >
          {loading ? (
            <InquiryListSkeleton />
          ) : filteredRows.length === 0 && !error ? (
            (hasMore || loadingMore) ? (
              <InquiryListSkeleton count={3} />
            ) : (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flex: 1,
                  gap: 1.25,
                  px: 2,
                  textAlign: 'center',
                }}
              >
                <Typography sx={{ color: colors.textSecondary, fontSize: '13px' }}>
                  {emptyMessage}
                </Typography>
              </Box>
            )
          ) : canViewLeads ? (
            <>
              {leadGroups.map((group) => (
                <InquiryLeadGroup
                  key={group.leadId}
                  group={group}
                  expandedIds={expandedIds}
                  onToggleInquiry={toggleExpanded}
                  onMarkSeen={markSeen}
                  onOpenLead={onOpenLead}
                  onOpenItem={onOpenItem}
                />
              ))}
              {orphans.map((row, index) => {
                const rowId = String(row.id || `${row.created_at}-${index}`);
                return (
                  <InquiryCard
                    key={rowId}
                    row={row}
                    expanded={expandedIds.has(rowId)}
                    onToggle={() => toggleExpanded(row)}
                    onOpenItem={onOpenItem}
                    titleMode="visitor"
                  />
                );
              })}
            </>
          ) : (
            filteredRows.map((row, index) => {
              const rowId = String(row.id || `${row.created_at}-${index}`);
              return (
                <InquiryCard
                  key={rowId}
                  row={row}
                  expanded={expandedIds.has(rowId)}
                  onToggle={() => toggleExpanded(row)}
                  onOpenItem={onOpenItem}
                  titleMode="visitor"
                />
              );
            })
          )}
          {hasMore ? <Box ref={sentinelRef} sx={{ height: 1 }} /> : null}
          {loadingMore && filteredRows.length > 0 ? (
            <InquiryListSkeleton count={1} />
          ) : null}
        </Box>
      </DialogContent>
      <DialogActions sx={appModalActionsSx}>
        <Button variant="outlined" onClick={onClose} sx={appModalSecondaryButtonSx}>
          Cerrar
        </Button>
      </DialogActions>
    </Dialog>
  );
}
